<template>
    <div style="width: 100%; height: 100%; display: flex;">
        <video v-if="realTime" ref="webrtcVideo" class="video-element" autoplay playsinline muted></video>
        <!-- HLS (.m3u8) com video.js -->
        <video v-else ref="videoPlayer" class="video-element video-js"></video>
    </div>

</template>
  
<script>
import videojs from "video.js";

export default {
    emits: ["failed", "ready", "error"],
    data() {
        return {
            player: null,          // video.js player (HLS)
            // WebRTC state
            pc: null,
            reconnectTimer: null,
            reconnectDelay: 2000,
            RECONNECT_DELAY_MAX: 30000,
        };
    },

    props: {
        id: {
            type: Number,
            default: 1,
        },
        frames: {
            type: Number,
            default: 1,
        },
        delay: {
            type: Number,
            default: 0,
        },
        interval: {
            type: Number,
            default: 500,
        },
        // For HLS: https://.../stream/index.m3u8
        // For WebRTC (realTime=true): https://.../mystream/whep
        source: {
            type: String,
            default: "",
        },
        height: {
            type: Number,
            required: false,
        },
        width: {
            type: Number,
            required: false,
            default: 0,
        },
        realTime: {
            type: Boolean,
            required: true,
            default: false,
        },
    },

    methods: {

  
        // ------------- Shared -------------
        retry() {
            this.$emit("failed", false);

            if (this.realTime) {
                // WebRTC retry
                this.startWebRTC();
            } else {
                // HLS retry
                if (this.player) {
                    this.player.src({
                        src: this.source,
                        type: "application/x-mpegURL",
                    });
                    this.player.play();
                } else {
                    this.initHlsPlayer();
                }
            }
        },

        // ------------- HLS (video.js) -------------

        initHlsPlayer() {
            const video = this.$refs.videoPlayer;
            if (!video) return;

            const options = {
                controls: false,
                autoplay: true,
                muted: true,
                preload: "auto",
                notSupportedMessage: "Ocorreu um erro ao reproduzir o vídeo",
                sources: [
                    {
                        src: this.source,
                        type: "application/x-mpegURL",
                    },
                ],
            };

            video.addEventListener("error", () => {
                this.$emit("failed", true);
            });

            this.player = videojs(video, options, () => {
                this.$el.querySelector("video").play();
            });
        },

        disposeHlsPlayer() {
            if (this.player) {
                this.player.dispose();
                this.player = null;
            }
        },

        // ------------- WebRTC (WHEP) -------------

        logStatus(msg) {
            // para debug, se quiser depois mandar para emit/console
            console.log(`[WebRTC ${this.id}] ${msg}`);
        },

        resetBackoff() {
            this.reconnectDelay = 2000;
        },

        scheduleReconnect(reason) {
            if (this.reconnectTimer || !this.realTime) return; // já agendado ou saiu do modo realtime
            this.logStatus(`Disconnected (${reason}), reconnect in ${this.reconnectDelay / 1000}s`);

            this.reconnectTimer = setTimeout(() => {
                this.reconnectTimer = null;
                this.startWebRTC();
                this.reconnectDelay = Math.min(
                    this.reconnectDelay * 2,
                    this.RECONNECT_DELAY_MAX
                );
            }, this.reconnectDelay);
        },

        clearReconnectTimer() {
            if (this.reconnectTimer) {
                clearTimeout(this.reconnectTimer);
                this.reconnectTimer = null;
            }
        },

        getIceServers() {
            const hasInternet = navigator.onLine;
            if (hasInternet) {
                this.logStatus("Online mode: STUN + host candidates");
                return [
                    { urls: "stun:stun.l.google.com:19302" },
                    // aqui no futuro você pode adicionar seu TURN
                ];
            } else {
                this.logStatus("Offline/LAN mode: host candidates only");
                return [];
            }
        },

        async startWebRTC() {
            // Só faz sentido se realTime = true
            console.log('init web rtc connection')
            if (!this.realTime) return;

            
            this.$emit("failed", false);
            this.clearReconnectTimer();

            // limpa conexão anterior
            if (this.pc) {
                try {
                    this.pc.close();
                } catch (e) {
                    console.log(e);
                }
                this.pc = null;
            }

            const videoEl = this.$refs.webrtcVideo;
            if (videoEl) {
                videoEl.srcObject = null;
            }

            // reinicia backoff em uma tentativa manual / nova
            this.resetBackoff();

            const whepUrl = this.source; // ex.: https://host:8889/VideoTS/whep
            if (!whepUrl) {
                this.logStatus("No WHEP URL provided");
                this.$emit("failed",true);
                return;
            }

            const iceServers = this.getIceServers();

            try {
                this.logStatus("Creating RTCPeerConnection...");
                const pc = new RTCPeerConnection({ iceServers });
                this.pc = pc;

                pc.addTransceiver("video", { direction: "recvonly" });

                pc.ontrack = (event) => {
                    if (!videoEl) return;
                    if (videoEl.srcObject !== event.streams[0]) {
                        videoEl.srcObject = event.streams[0];
                        this.logStatus("Stream playing");
                    }
                };

                pc.oniceconnectionstatechange = () => {
                    this.logStatus(`ICE state: ${pc.iceConnectionState}`);
                    if (pc.iceConnectionState === "connected") {
                        this.resetBackoff();
                        this.$emit("failed", false);
                    } else if (pc.iceConnectionState === "failed") {
                        this.$emit("failed", true);
                        //this.scheduleReconnect("ICE failed");
                    } else if (pc.iceConnectionState === "disconnected") {
                        this.$emit("failed", true);
                        //this.scheduleReconnect("ICE disconnected");
                    } else if (pc.iceConnectionState === "closed") {
                        //this.scheduleReconnect("ICE closed");
                    }
                };

                const offer = await pc.createOffer();
                await pc.setLocalDescription(offer);

                this.logStatus("Sending SDP offer to MediaMTX...");

                const res = await fetch(whepUrl, {
                    method: "POST",
                    headers: { "Content-Type": "application/sdp" },
                    body: offer.sdp,
                });

                if (!res.ok) {
                    throw new Error("HTTP error " + res.status);
                }

                const answerSdp = await res.text();

                await pc.setRemoteDescription({
                    type: "answer",
                    sdp: answerSdp,
                });

                this.logStatus("Negotiation done, waiting for media...");
            } catch (err) {
                console.error("Failed to start WebRTC stream:", err);
                this.$emit("failed", true);
                //this.scheduleReconnect("startup error");
            }
        },

        disposeWebRTC() {
            this.clearReconnectTimer();
            if (this.pc) {
                try {
                    this.pc.close();
                } catch (e) {
                    console.log(e);
                }
                this.pc = null;
            }
            const videoEl = this.$refs.webrtcVideo;
            if (videoEl) {
                videoEl.srcObject = null;
            }
        },
    },

    mounted() {
        console.log("Video source:", this.source);

        if (this.realTime) {
            this.startWebRTC();
        } else {
            this.initHlsPlayer();
        }
    },

    watch: {
        realTime(newVal, oldVal) {
            // troca de modo em runtime
            if (newVal === oldVal) return;

            // limpando estados anteriores
            this.$emit("failed", false);

            if (newVal) {
                // indo para WebRTC
                this.disposeHlsPlayer();
                this.startWebRTC();
            } else {
                // voltando para HLS
                this.disposeWebRTC();
                this.initHlsPlayer();
            }
        },

        source(newVal, oldVal) {
            if (newVal === oldVal) return;

            if (this.realTime) {
                // nova URL WHEP
                this.startWebRTC();
            } else if (this.player) {
                // HLS: troca de m3u8
                this.player.src({
                    src: newVal,
                    type: "application/x-mpegURL",
                });
                this.player.play();
            }
        },
    },

    beforeDestroy() {
        this.disposeHlsPlayer();
        this.disposeWebRTC();
    },
};
</script>
  
<style>
.video-container {
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    /* center contents */
    align-items: center;
    justify-content: center;
    background: #000;
    /* letterbox bars */
}

/* Our video element: keep aspect ratio, no cropping */
.video-element {
    width: 100%;
    height: 100%;
    object-fit: contain;
    /* show whole frame, may add black bars */
}

/* If video.js wraps with .vjs-tech inside, keep same behavior */
.video-js .vjs-tech {
    max-width: 100% !important;
    max-height: 100% !important;
    width: auto !important;
    height: auto !important;
    object-fit: contain !important;
    position: relative;
}

.video-js {
    width: 100% !important;
    height: 100% !important;
    display: flex;
    align-items: center;
    justify-content: center;
}
</style>
  
<style scoped lang="scss">
.videoPlayerErrorOverlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 2;

    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    gap: 1.25rem;

    background: #121212;
    background: linear-gradient(135deg,
            #8f2437 25%,
            #520b17 25%,
            #520b17 50%,
            #8f2437 50%,
            #8f2437 75%,
            #520b17 75%,
            #520b17);
    background-size: 40px 40px;

    animation: move 9s linear infinite;

    svg {
        font-size: 2.5rem;
    }

    button {
        border: none;
        padding: 0.25rem 1rem;
        border-radius: 0.25rem;
        box-shadow: rgba(0, 0, 0, 0.25) 0px 3px 8px;
        transition: 0.3s ease-in-out;

        svg {
            font-size: 1rem;
        }

        &:hover {
            box-shadow: rgba(0, 0, 0, 0.25) 0px 6px 14px;
            background-color: #ffd8db;
        }
    }
}

@keyframes move {
    0% {
        background-position: 0 0;
    }

    100% {
        background-position: 40px 40px;
    }
}
</style>
  